'use client';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { SxProps } from '@mui/material/styles';
import { grey } from '@mui/material/colors';
import Divider from '@mui/material/Divider';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import Box from '@mui/material/Box';
import { useIfaStore } from '@ifa/store';
import { SubmissionDetail, TooltipMessages, UISubmissionStatus, UISubmissionStatusLabels } from '@ifa/types';

import { btmColors } from '../theme/colors';
import { getFormattedGrid, getFormattedPatientId } from '../../utils';

type ContentItemProps = {
    label: string;
    children: React.ReactNode;
    containerSx?: SxProps;
    childrenSx?: SxProps;
    headerSx?: SxProps;
    testId?: string;
};

const ContentItem = ({ children, containerSx, headerSx, childrenSx, label, testId }: ContentItemProps) => {
    return (
        <Box paddingY={1} sx={{ ...containerSx }}>
            <Typography variant="body1" sx={{ color: grey[600], ...headerSx }}>
                <strong>{label}</strong>
            </Typography>
            <Typography variant="body1" sx={{ color: grey[700], ...childrenSx }} data-cy={testId}>
                {children}
            </Typography>
        </Box>
    );
};

export const getFormattedStatus = (item: SubmissionDetail | undefined): string => {
    const status = item?.status;
    const mostRecentEvent = item?.events?.[0];

    if (!status) return '';

    if (status === 'FAILED' && mostRecentEvent?.metadata?.message) {
        return `${UISubmissionStatusLabels[status]} - ${mostRecentEvent.metadata.message}`;
    }

    return `${TooltipMessages[status]}`;
};

export default function Drawer() {
    const closeDrawer = useIfaStore(state => state.drawer.closeDrawer);
    const drawerStatus = useIfaStore(state => state.drawer.opened);
    const item = useIfaStore(state => state.drawer.currentItem);

    // TODO: style the empty state
    if (typeof item === 'string') {
        return (
            <Box sx={{ padding: 2 }}>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        paddingY: 2,
                        paddingX: 1,
                    }}
                >
                    <IconButton
                        onClick={closeDrawer}
                        aria-label="Close Drawer"
                        data-cy="drawer-close"
                        name="Close Drawer"
                        role="button"
                        size="small"
                    >
                        <CloseRoundedIcon />
                    </IconButton>
                </Box>
                <Typography variant="h6" sx={{ color: grey[600] }}>
                    {item}
                </Typography>
            </Box>
        );
    }

    //TODO: handle null item higher up instead of at individual values
    return (
        <Box sx={{ paddingX: 2, paddingY: 4 }} data-cy="details-drawer">
            {/* Drawer Header */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar
                        sx={{
                            width: '5rem',
                            height: '5rem',
                            fontSize: '2rem',
                            bgcolor: btmColors.blue,
                        }}
                        aria-label="Form name"
                    >
                        {item?.formType}
                    </Avatar>
                    <Box sx={{ ml: 2 }}>
                        <Typography variant="body1" sx={{ fontSize: '1rem', color: grey[600] }}>
                            <strong>Patient ID:</strong>
                        </Typography>
                        <Typography sx={{ color: grey[700], fontSize: '2rem', fontWeight: 600, lineHeight: '1.75rem' }}>
                            {getFormattedPatientId(item?.rid)}
                        </Typography>
                    </Box>
                </Box>
                <Box>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            paddingY: 2,
                            paddingX: 1,
                        }}
                    >
                        <IconButton
                            tabIndex={drawerStatus ? 0 : -1}
                            onClick={closeDrawer}
                            aria-label="Close Drawer"
                            data-cy="drawer-close"
                            name="Close Drawer"
                            role="button"
                            size="small"
                        >
                            <CloseRoundedIcon />
                        </IconButton>
                    </Box>
                </Box>
            </Box>
            <Divider sx={{ marginY: 4 }} />
            {item?.createdOn ? (
                <ContentItem label="Submission date">
                    {new Date(item?.createdOn).toLocaleString('en', {
                        month: 'numeric',
                        day: 'numeric',
                        year: 'numeric',
                        hour: 'numeric',
                        minute: 'numeric',
                    })}
                </ContentItem>
            ) : null}
            <ContentItem label="Submitted by">{item?.created}</ContentItem>
            <ContentItem label="TC ID">{item?.tcId}</ContentItem>
            <ContentItem label="GRID">{item?.grid ? getFormattedGrid(item?.grid) : '-'}</ContentItem>
            <ContentItem label="Status" testId="drawer-status">
                {getFormattedStatus(item)}
            </ContentItem>
            <Box paddingY={1}>
                <Typography variant="body1" sx={{ color: grey[600], marginBottom: '0.5rem' }}>
                    <strong>Notification email recipients</strong>
                </Typography>
                {item?.notify.map((email, index) => (
                    <Chip label={email} key={index} variant="filled" sx={{ mr: 1, mb: 1, fontWeight: 600 }} />
                ))}
                {/* TO-DO: implement hiding/diplaying chips in future release */}
                {/* <Typography variant="h6" sx={{ display: 'inline' }}>
                    + 3 more
                </Typography> */}
            </Box>
        </Box>
    );
}
